/* eslint-disable regex/invalid -- ignore `useResourceBasePath` */
import { G } from '@mobily/ts-belt';
import { makeStyles } from '@mui/styles';
import { get } from 'lodash';
import { FieldProps, Labeled, Link, RaRecord, useRecordContext, useTranslate } from 'react-admin';

import { IdField } from '@sh4res/bo-common/fields';
import type { IdFieldQueryParamsArrayLike } from '@sh4res/bo-common/fields/IdField';
import { Resources } from '@sh4res/bo-common/utils';
import { buildLink } from '@sh4res/bo-common/utils/build_link';

type IdListFieldProps = FieldProps & {
  reference: Resources;
  source: string;
  maxItems?: number;
  target?: string;
  rel?: string;
  testId?: string;
  textSource?: string;
  showAllLink?: boolean;
  hideLabel?: boolean;
  queryParams?: Record<string, string | number | boolean | IdFieldQueryParamsArrayLike>;
};

/**
 * Displays a list of linked IDs or objects as clickable links.
 *
 * **Primitive array mode** (no `textSource`): `source` points to an array of IDs.
 * The ID is used as both the link value and display text.
 * ```
 * record = { wm_company_ids: [1, 2, 3] }
 * <IdListField source="wm_company_ids" reference="wm/companies" />
 * // → "1, 2, 3" each linking to /wm/companies/{id}/show
 * ```
 *
 * **Object array mode** (`textSource` provided): `source` points to an array of records.
 * Each object's `id` field is used for the link; `textSource` is used for display text.
 * ```
 * record = { profiles: [{ id: 1, name: 'Conservative' }, { id: 2, name: 'Balanced' }] }
 * <IdListField source="profiles" textSource="name" reference="b2b/investor_profile" />
 * // → "Conservative, Balanced" each linking to /b2b/investor_profile/{id}/show
 * ```
 */
export const IdListField = (props: IdListFieldProps) => {
  const {
    source,
    reference,
    maxItems,
    label,
    testId,
    textSource,
    showAllLink = true,
    hideLabel = false,
    queryParams = {},
  } = props;
  const record = useRecordContext(props);
  const classes = useStyles();
  const translate = useTranslate();

  const rawValues = get(record, source);

  if (G.isNullable(rawValues)) {
    return null;
  }

  const isObjectMode = textSource !== undefined;

  type NormalizedItem = { record: RaRecord; source: string; id: string | number; key: string };

  const normalize = (item: unknown, index: number): NormalizedItem => {
    if (isObjectMode) {
      const obj = item as RaRecord;
      return { record: obj, source: 'id', id: obj.id, key: `${reference}-${source}-${obj.id}` };
    }
    const value = item as string | number;
    return {
      record: { id: index, value },
      source: 'value',
      id: value,
      key: `${reference}-${source}-${value}`,
    };
  };

  let items: NormalizedItem[] = (rawValues as unknown[]).map(normalize);

  const truncated = maxItems !== undefined && items.length > maxItems;
  const allIds = items.map((item) => item.id);
  if (truncated) {
    items = items.slice(0, maxItems);
  }

  const content = (
    <div className={classes.root}>
      {items.map(({ record: itemRecord, source: itemSource, key }, index) => (
        <span key={key} className={classes.idField}>
          <IdField
            data-testid={testId}
            record={itemRecord}
            source={itemSource}
            textSource={textSource}
            reference={reference}
            queryParams={queryParams}
          />
          {index < items.length - 1 ? ',' : ''}
        </span>
      ))}
      {truncated ? '...' : ''}
      {showAllLink && items.length > 1 && (
        <Link
          to={buildLink(reference, 'list', { filter: { id: allIds } })}
          className={classes.allLink}
          target="_blank"
          rel="noopener noreferrer">
          all
        </Link>
      )}
      {items.length === 0 && props.emptyText ? translate(props.emptyText) : null}
    </div>
  );

  if (label !== undefined && !hideLabel) {
    return (
      <Labeled label={label}>
        <>{content}</>
      </Labeled>
    );
  }

  return content;
};

const useStyles = makeStyles((theme) => ({
  idField: {
    paddingRight: theme.spacing(),
    marginTop: 5,
    marginBottom: 5,
  },
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    '&:hover $allLink': {
      opacity: 1,
    },
  },
  allLink: {
    opacity: 0,
    color: theme.palette.secondary.main,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));
